import type { ApiErrorBody, ErrorCode } from '@l2u/shared/errors';
import { getActiveStation } from './activeStation.ts';
import { getAdminAccessToken, setAdminAccessToken } from './auth/adminToken.ts';
import { getCompanyAccessToken, setCompanyAccessToken } from './auth/companyToken.ts';
import { getAdminSupabase, getSupabase } from './supabase.ts';

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');

export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode | 'network';
  readonly details: unknown;

  constructor(status: number, code: ErrorCode | 'network', message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

type Init = Omit<RequestInit, 'body'> & {
  body?: unknown;
  query?: Record<string, string | number | undefined | null>;
};

async function request(path: string, init: Init = {}, headers: Record<string, string> = {}): Promise<unknown> {
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  if (init.query) {
    for (const [k, v] of Object.entries(init.query)) {
      if (v === undefined || v === null) continue;
      url.searchParams.set(k, String(v));
    }
  }

  const finalHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...headers,
    ...(init.headers as Record<string, string> | undefined),
  };

  const fetchInit: RequestInit = { headers: finalHeaders };
  if (init.method !== undefined) fetchInit.method = init.method;
  if (init.credentials !== undefined) fetchInit.credentials = init.credentials;
  if (init.body !== undefined && init.body !== null) {
    fetchInit.body = JSON.stringify(init.body);
    finalHeaders['Content-Type'] = 'application/json';
  }

  let res: Response;
  try {
    res = await fetch(url.toString(), fetchInit);
  } catch (err) {
    throw new ApiError(0, 'network', err instanceof Error ? err.message : 'Network error');
  }

  if (res.status === 204) return null;

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    const body = parsed as ApiErrorBody | null;
    const code = (body?.error?.code as ErrorCode | undefined) ?? 'internal';
    const message = body?.error?.message ?? `Request failed (${res.status})`;
    throw new ApiError(res.status, code, message, body?.error?.details);
  }

  return parsed;
}

function bearerHeader(token: string | null): Record<string, string> {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Single-flight refresh: if a 401 lands while another refresh is already
// running, all callers await the same promise rather than racing concurrent
// refreshSession() calls (which can rotate the refresh token).
let companyRefreshInFlight: Promise<string | null> | null = null;
let adminRefreshInFlight: Promise<string | null> | null = null;

async function refreshCompanySession(): Promise<string | null> {
  if (companyRefreshInFlight) return companyRefreshInFlight;
  companyRefreshInFlight = (async () => {
    try {
      const supabase = getSupabase();
      const { data, error } = await supabase.auth.refreshSession();
      if (error || !data.session) return null;
      setCompanyAccessToken(data.session.access_token);
      return data.session.access_token;
    } catch {
      return null;
    }
  })();
  try {
    return await companyRefreshInFlight;
  } finally {
    companyRefreshInFlight = null;
  }
}

async function refreshAdminSession(): Promise<string | null> {
  if (adminRefreshInFlight) return adminRefreshInFlight;
  adminRefreshInFlight = (async () => {
    try {
      const supabase = getAdminSupabase();
      const { data, error } = await supabase.auth.refreshSession();
      if (error || !data.session) return null;
      setAdminAccessToken(data.session.access_token);
      return data.session.access_token;
    } catch {
      return null;
    }
  })();
  try {
    return await adminRefreshInFlight;
  } finally {
    adminRefreshInFlight = null;
  }
}

async function requestWithRefresh(
  path: string,
  init: Init,
  getToken: () => string | null,
  refresh: () => Promise<string | null>,
): Promise<unknown> {
  try {
    return await request(path, init, bearerHeader(getToken()));
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401) throw err;
    const refreshed = await refresh();
    if (!refreshed) throw err;
    return await request(path, init, bearerHeader(refreshed));
  }
}

// Admin requests carry the sidebar's Active Station so the API narrows vendor
// visibility server-side. Harmless on non-vendor endpoints. The value is
// URI-encoded because fetch headers reject code points > U+00FF (e.g. an
// en-dash in a station name); the API decodes it back.
function withActiveStation(init: Init): Init {
  const station = getActiveStation();
  if (!station) return init;
  return {
    ...init,
    headers: {
      ...(init.headers as Record<string, string> | undefined),
      'X-Active-Station': encodeURIComponent(station),
    },
  };
}

export const adminApi = {
  get<T>(path: string, init?: Init): Promise<T> {
    return requestWithRefresh(path, withActiveStation({ ...init, method: 'GET' }), getAdminAccessToken, refreshAdminSession) as Promise<T>;
  },
  post<T>(path: string, body?: unknown, init?: Init): Promise<T> {
    return requestWithRefresh(path, withActiveStation({ ...init, method: 'POST', body }), getAdminAccessToken, refreshAdminSession) as Promise<T>;
  },
  patch<T>(path: string, body?: unknown, init?: Init): Promise<T> {
    return requestWithRefresh(path, withActiveStation({ ...init, method: 'PATCH', body }), getAdminAccessToken, refreshAdminSession) as Promise<T>;
  },
  put<T>(path: string, body?: unknown, init?: Init): Promise<T> {
    return requestWithRefresh(path, withActiveStation({ ...init, method: 'PUT', body }), getAdminAccessToken, refreshAdminSession) as Promise<T>;
  },
  delete<T>(path: string, init?: Init): Promise<T> {
    return requestWithRefresh(path, withActiveStation({ ...init, method: 'DELETE' }), getAdminAccessToken, refreshAdminSession) as Promise<T>;
  },
};

export const companyApi = {
  get<T>(path: string, init?: Init): Promise<T> {
    return requestWithRefresh(path, { ...init, method: 'GET' }, getCompanyAccessToken, refreshCompanySession) as Promise<T>;
  },
  post<T>(path: string, body?: unknown, init?: Init): Promise<T> {
    return requestWithRefresh(path, { ...init, method: 'POST', body }, getCompanyAccessToken, refreshCompanySession) as Promise<T>;
  },
  patch<T>(path: string, body?: unknown, init?: Init): Promise<T> {
    return requestWithRefresh(path, { ...init, method: 'PATCH', body }, getCompanyAccessToken, refreshCompanySession) as Promise<T>;
  },
  put<T>(path: string, body?: unknown, init?: Init): Promise<T> {
    return requestWithRefresh(path, { ...init, method: 'PUT', body }, getCompanyAccessToken, refreshCompanySession) as Promise<T>;
  },
  delete<T>(path: string, init?: Init): Promise<T> {
    return requestWithRefresh(path, { ...init, method: 'DELETE' }, getCompanyAccessToken, refreshCompanySession) as Promise<T>;
  },
};

export const publicApi = {
  get<T>(path: string, init?: Init): Promise<T> {
    return request(path, { ...init, method: 'GET' }) as Promise<T>;
  },
  post<T>(path: string, body?: unknown, init?: Init): Promise<T> {
    return request(path, { ...init, method: 'POST', body }) as Promise<T>;
  },
};

export async function uploadFileToSignedUrl(signedUrl: string, file: File): Promise<void> {
  const res = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!res.ok) {
    throw new ApiError(res.status, 'internal', `Upload failed (${res.status})`);
  }
}
